import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FundPerformance } from '../../domain/fund-performance/dto/FundPerformance';
import { cloneDeep } from 'lodash';
import { LocalDate } from '../pojo/date-variable';
import { SwitcherNumberType } from "../component/numberswitcher/utils/number-switcher-common-classes";

@Injectable({
  providedIn: 'root',
})
export class HorNavbarService implements OnDestroy {

  periods: Date[] = undefined;
  selectedPeriod: Date = undefined;
  numberSwitcher: SwitcherNumberType = SwitcherNumberType.REGULAR;
  dateSwitcher: string = undefined;

  private dateSwitcherSource = new BehaviorSubject(this.dateSwitcher);
  private numberSwitcherSource = new BehaviorSubject(this.numberSwitcher);
  private selectedPeriodSource = new BehaviorSubject(this.selectedPeriod);
  private periodsSource = new BehaviorSubject(this.periods);
  private customShowElementsSource = new BehaviorSubject(undefined);
  private selectedFundPerformanceSource = new BehaviorSubject<FundPerformance>(undefined);
  private updateFundPerformanceSource = new BehaviorSubject<FundPerformance>(undefined);
  private fetchAllFundPerformancesFromBackendSource = new BehaviorSubject<UpdateAllFPsWrapper>(undefined);
  private fundPerformancesSource = new BehaviorSubject<FundPerformance[]>(undefined);

  getPeriods = this.periodsSource.asObservable();
  getSelectedPeriod = this.selectedPeriodSource.asObservable();
  getDateSwitcher = this.dateSwitcherSource.asObservable();
  getNumberSwitcher = this.numberSwitcherSource.asObservable();
  getCustomShowElements = this.customShowElementsSource.asObservable();
  getSelectedFundPerformance = this.selectedFundPerformanceSource.asObservable();
  getUpdateFundPerformance = this.updateFundPerformanceSource.asObservable();
  /**
   * Provides a trigger for "FundPerformance" and "Hornavbar" components to fetch the fund performances from backend.
   * If the observable value is "isSavedPeriodToBeSelected", it means that "Hornavbar" should keep the previous selected period if possible.
   * Otherwise, the first period of list will be selected.
   */
  getFetchAllFundPerformancesFromBackend = this.fetchAllFundPerformancesFromBackendSource.asObservable();
  getFundPerformances = this.fundPerformancesSource.asObservable();

  // FPs are filtered by isVisible field.
  setFundPerformances(fps: FundPerformance[]): void {
    this.fundPerformancesSource.next(cloneDeep(fps)); // do not send original data to subscribers. Send copy instead.
  }

  fetchAllFundPerformancesFromBackend(type: UpdateAllFPsAction = 'default', selectedPeriod?: LocalDate): void {
    this.fetchAllFundPerformancesFromBackendSource.next(
      {
        type,
        selectedPeriod,
      });
  }

  updateFundPerformance(fundPerformance: FundPerformance): void {
    this.updateFundPerformanceSource.next(fundPerformance);
  }

  setPeriods(periods: Date[]): void {
    if (periods !== null) {
      periods.sort((el1, el2) => el1.toString().localeCompare(el2.toString()));
      if (periods === this.periods) {
        return;
      }
      this.periods = periods;
      this.periodsSource.next(periods);
    }
  }

  /**
   * This function implies setting the new value to the periodService as well.
   * Example:
   * <pre>
   *  this.periodService.set(String(fp.period));
   *  this.navbarService.setSelectedPeriod(this.selected.fp.period);
   * </pre>
   * @param period to be selected
   */
  setSelectedPeriod(period: Date): void {
    this.selectedPeriod = period;
    this.selectedPeriodSource.next(period);
  }

  setNumberSwitcher(s: SwitcherNumberType): void {
    if (s !== null) {
      this.numberSwitcher = s;
      this.numberSwitcherSource.next(s);
    }
  }

  setDateSwitcher(s: string): void {
    if (s !== null) {
      this.dateSwitcher = s;
      this.dateSwitcherSource.next(s);
    }
  }

  resetPeriods(): void {
    this.periodsSource.next(undefined);
    this.selectedPeriodSource.next(undefined);
  }

  ngOnDestroy(): void {
    this.selectedPeriod = null;
    this.periods = null;
  }

  setSelectedFundPerformance(fundPerformance: FundPerformance): void {
    this.selectedFundPerformanceSource.next(fundPerformance);
  }
}

export type UpdateAllFPsAction = 'isSavedPeriodToBeSelected' | 'default';
export interface UpdateAllFPsWrapper {
  type: UpdateAllFPsAction;
  selectedPeriod: LocalDate;
}
